import { useQuery } from '@tanstack/react-query';
import { trackerAPI } from '../api/tracker.api';

export interface JustifyingItem {
  // Note: 'todo' source_type removed - "À faire" is now based on tracker_communication_metadata
  source_type: 'communication' | 'status_based';
  communication_type: string | null;
  communication_id: string | null;
  priority: string;
  content: string | null;
  is_done: boolean;
  created_at: string;
}

/**
 * Hook to fetch items that justify why an affaire appears in "À faire - Ouvert" for the current user.
 * Used to highlight the justifying communications in the chronologic view.
 *
 * @param affaireId - The affaire ID
 * @param enabled - Whether to enable the query (default: true)
 */
export const useJustifyingItems = (affaireId: number | null, enabled = true) => {
  const query = useQuery({
    queryKey: ['justifying-items', affaireId],
    queryFn: async () => {
      if (!affaireId) return null;
      const response = await trackerAPI.getJustifyingItems(affaireId);
      return response.data;
    },
    enabled: enabled && affaireId !== null,
    staleTime: 30 * 1000, // Consider data stale after 30 seconds
    refetchOnWindowFocus: true,
  });

  // Build a Set of communication IDs that should be highlighted
  const highlightedComIds = new Set<string>();

  if (query.data) {
    query.data.forEach(item => {
      if (item.source_type === 'communication' && item.communication_id) {
        // For communications, add the ID with type prefix for unique identification
        highlightedComIds.add(`${item.communication_type}:${item.communication_id}`);
      }
    });
  }

  // Check if justification is status-based (contains "à achever" etc)
  const isStatusBased = query.data?.some(item => item.source_type === 'status_based') ?? false;

  // Note: hasTodoTag removed - "À faire" is now based on tracker_communication_metadata

  return {
    items: query.data ?? [],
    highlightedComIds,
    isStatusBased,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

export default useJustifyingItems;
